import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NavHeader from '../shared/Nav-Header';
import styles from './applied-interns.module.css';

const GuestAppliedInterns = () => {
    const [appliedJobs, setAppliedJobs] = useState([]);
    const [notification, setNotification] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const token = useMemo(() => localStorage.getItem('token'), []);
    
    const handleJobClick = (jobId) => {
        navigate(`/job-details/${jobId}`);
    };

    const handleLogin = () => {
        navigate('/login');
    };

    useEffect(() => {
        const fetchAppliedJobs = async () => {
            try {
                if (!token) {
                    setIsLoggedIn(false);
                    setLoading(false);
                    return;
                }
                setIsLoggedIn(true);
            } catch (error) {
                // console.error('Error fetching applied jobs:', error);
                setNotification('Error fetching applied jobs. Please try again.');
                setIsLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        fetchAppliedJobs();
    }, [navigate]);

    const handleCloseNotification = () => {
        setNotification('');
    };

    // Demo data for display when not logged in
    const demoJobs = [
        {
            _id: 'demo1',
            jobId: {
                _id: 'job1',
                title: 'Frontend Developer Intern',
                company: {
                    name: 'Tech Solutions Inc.',
                    logo: '/default-profile.png'
                },
                description: 'Join our dynamic team to work on cutting-edge web applications using React and modern JavaScript frameworks.',
                location: 'San Francisco, CA'
            },
            status: 'Applied'
        },
        {
            _id: 'demo2',
            jobId: {
                _id: 'job2',
                title: 'Backend Developer Intern',
                company: {
                    name: 'DataFlow Systems',
                    logo: '/default-profile.png'
                },
                description: 'Work with our backend team to develop scalable APIs and database solutions using Node.js and MongoDB.',
                location: 'New York, NY'
            },
            status: 'Under Review'
        },
        {
            _id: 'demo3',
            jobId: {
                _id: 'job3',
                title: 'UX/UI Design Intern',
                company: {
                    name: 'Creative Studios',
                    logo: '/default-profile.png'
                },
                description: 'Create intuitive user interfaces and engaging user experiences for our mobile and web applications.',
                location: 'Los Angeles, CA'
            },
            status: 'Rejected',
            rejectionReasons: ['Portfolio needs improvement', 'Limited experience with design tools']
        }
    ];

    const displayJobs = isLoggedIn ? appliedJobs : demoJobs;

    return (
        <>
            <NavHeader />
            <div className={styles.appliedInternsContainer}>
                {!isLoggedIn && !loading && (
                    <div className={styles.loginOverlay}>
                        <div className={styles.loginPrompt}>
                            <h3>Login Required</h3>
                            <p>Please log in to view your applied internships and track your application status.</p>
                            <button 
                                className={styles.loginButton}
                                onClick={handleLogin}
                            >
                                Login
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner}></div>
                        <p>Loading your applied internships...</p>
                    </div>
                )}

                <div className={`${styles.appliedInternsContent} ${(!isLoggedIn && !loading) ? styles.blurred : ''}`}>
                    {notification && (
                        <div className={styles.notification}>
                            <p>{notification}</p>
                            <button 
                                onClick={handleCloseNotification}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>
                    )}
                    
                    <h2 className={styles.pageTitle}>
                        {isLoggedIn ? 'Your Applied Jobs' : 'Applied Jobs Preview'}
                    </h2>
                    
                    {!isLoggedIn && !loading && (
                        <div className={styles.previewNotice}>
                            <p>🔒 This is a preview of what your applied jobs would look like. Login to see your actual applications.</p>
                        </div>
                    )}
                    
                    <div className={styles.jobsList}>
                        {displayJobs.map(job => (
                            <div 
                                key={job._id} 
                                className={styles.jobCard}
                                onClick={() => isLoggedIn && handleJobClick(job.jobId._id)}
                            >
                                <div className={styles.jobHeader}>
                                    <img 
                                        src={job.jobId?.company.logo 
                                            ? job.jobId.company.logo.replace('/upload/', '/upload/w_192,h_200,c_fill/') 
                                            : '/default-profile.png'
                                        }
                                        alt={`${job.jobId?.company.name} logo`} 
                                        className={styles.companyLogo} 
                                    />
                                    <div className={styles.jobTitleSection}>
                                        <h3 className={styles.jobTitle}>{job.jobId?.title}</h3>
                                        <p className={styles.companyName}>{job.jobId?.company.name}</p>
                                    </div>
                                    <div className={styles.statusBadge}>
                                        <span className={`${styles.status} ${styles[job.status?.toLowerCase().replace(' ', '')]}`}>
                                            {job.status}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className={styles.jobDetails}>
                                    <p className={styles.jobDescription}>
                                        {job.jobId?.description?.split('\n').slice(0, 2).join(' ')}...
                                    </p>
                                    <p className={styles.jobLocation}>
                                        <span className={styles.label}>Location:</span> {job.jobId?.location}
                                    </p>
                                    <p className={styles.jobId}>
                                        <span className={styles.label}>Job ID:</span> {job._id}
                                    </p>
                                </div>
                                
                                {job.jobId?.rejectionReasons?.length > 0 && (
                                    <div className={styles.rejectionSection}>
                                        <h4 className={styles.rejectionTitle}>Rejection Reasons:</h4>
                                        <ul className={styles.rejectionList}>
                                            {job.jobId.rejectionReasons.map((reason, index) => (
                                                <li key={index} className={styles.rejectionItem}>
                                                    {reason}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {displayJobs.length === 0 && isLoggedIn && (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyStateIcon}>📋</div>
                            <h3>No Applied Jobs Yet</h3>
                            <p>You haven't applied to any internships yet. Start exploring opportunities!</p>
                            <button 
                                className={styles.exploreButton}
                                onClick={() => navigate('/jobs')}
                            >
                                Explore Jobs
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default GuestAppliedInterns;